import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { GroupResponse, PaginatedGroupResponse } from '../types/group';
import { useAuthInfo } from '@propelauth/react';

// Simple utility for conditional logging
const DEBUG = env.DEBUG_MODE;
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

// Cache for storing ETags
const etagCache = new Map<string, string>();

export interface Group extends Omit<GroupResponse, 'unread_count'> {
  recent_activity: Array<{
    id: string;
    type: string;
    text: string;
    created_at: string;
    user_id: string | null;
    user_name?: string;
    user_avatar?: string;
  }>;
  unread_activity_count: number;
  unread_messages_count: number;
}

export const useGroups = (accessToken: string, options: { isVisible?: boolean } = {}) => {
  const { isVisible = true } = options;
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();
  const initialFetchDone = useRef(false);

  const getValidToken = () => {
    if (accessToken && accessToken.length > 0) {
      return accessToken;
    } else if (authInfo?.accessToken) {
      debugLog('[useGroups] Using accessToken from useAuthInfo');
      return authInfo.accessToken;
    }
    return null;
  };

  // Fetch groups query
  const {
    data: groupsData,
    isLoading: loading,
    error: queryError,
    refetch: refreshQuery
  } = useQuery({
    queryKey: ['groups'],
    queryFn: async (): Promise<{
      groups: Group[];
      currentPage: number;
      totalPages: number;
      totalItems: number;
      pageSize: number;
    }> => {
      const token = getValidToken();
      if (!token) {
        console.warn('[useGroups] fetchGroups called without valid token');
        throw new Error('Authentication token missing or invalid');
      }

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };

      // Add If-None-Match header if we have a cached ETag
      const cachedETag = etagCache.get('groups');
      if (cachedETag) {
        headers['If-None-Match'] = cachedETag;
      }

      const response = await fetch(`${env.API_URL}/api/groups`, {
        headers,
        credentials: 'include',
        cache: 'no-cache', // Forces validation with server
      });

      // If we get a 304 Not Modified, use the cached data
      if (response.status === 304) {
        console.log('Using cached groups data (304 Not Modified)');
        const cachedData = queryClient.getQueryData<{
          groups: Group[];
          currentPage: number;
          totalPages: number;
          totalItems: number;
          pageSize: number;
        }>(['groups']);

        if (cachedData) {
          return cachedData;
        }
      }

      // Store the new ETag for future requests
      const newETag = response.headers.get('ETag');
      if (newETag) {
        etagCache.set('groups', newETag);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PaginatedGroupResponse = await response.json();
      if (DEBUG) {
        debugLog('API response data (paginated object):', data);
      }

      if (!data || !data.items || !Array.isArray(data.items)) {
        console.error('Paginated API response is not valid or missing items array:', data);
        throw new Error('Invalid response format from API');
      }

      return {
        groups: data.items as GroupResponse[] as Group[],
        currentPage: data.page,
        totalPages: data.pages,
        totalItems: data.total,
        pageSize: data.size
      };
    },
    enabled: isVisible && !!(getValidToken()),
    staleTime: 300000, // 5 minutes
  });

  // Extract data from query result with defaults
  const groups = groupsData?.groups || [];
  const currentPage = groupsData?.currentPage || 1;
  const totalPages = groupsData?.totalPages || 0;
  const totalItems = groupsData?.totalItems || 0;
  const pageSize = groupsData?.pageSize || 10;

  // Convert React Query error to string format for API compatibility
  const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

  // Delete group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: async (groupId: number) => {
      const token = getValidToken();
      if (!token) {
        console.warn('[useGroups] deleteGroup called without valid token');
        return { success: false, error: 'Authentication token missing or invalid' };
      }

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return { success: true, groupId };
      } else {
        // Handle different status codes
        if (response.status === 403) {
          return {
            success: false,
            error: data.detail || "You don't have permission to delete this group. Only the group owner can delete groups."
          };
        } else {
          return {
            success: false,
            error: data.detail || "Failed to delete group"
          };
        }
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        // Update the groups in the cache by removing the deleted group
        queryClient.setQueryData(['groups'], (oldData: any) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            groups: oldData.groups.filter((group: Group) => group.id !== result.groupId),
            totalItems: oldData.totalItems - 1,
          };
        });
      }
    },
  });

  // Maintain API compatibility with original hook
  const refresh = (page: number = 1) => {
    // Update the query with new parameters and trigger refetch
    queryClient.setQueryData(['groups'], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        currentPage: page
      };
    });
    refreshQuery();
  };

  const handlePageChange = (newPage: number) => {
    // Update the current page in the query data
    queryClient.setQueryData(['groups'], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        currentPage: newPage
      };
    });
    refreshQuery();
  };

  const deleteGroup = async (groupId: number) => {
    return deleteGroupMutation.mutateAsync(groupId);
  };

  const setPageSize = (newPageSize: number) => {
    // Update the page size in the query data
    queryClient.setQueryData(['groups'], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pageSize: newPageSize
      };
    });
    refreshQuery();
  };

  return {
    groups,
    loading,
    error,
    refresh,
    deleteGroup,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    handlePageChange,
    setPageSize,
  };
}; 